import { memo, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * CurtainReveal — two theatre curtain halves over a gold valance that part to
 * reveal their `children`. Drives the big ceremonial reveals (deal intro, the
 * final winner) by wrapping the content and flipping `open`.
 *
 * Closed: the halves meet in the middle. `open` slides each half fully off its
 * side over 1.2s on the --ease-dramatic curve. Reduced-motion swaps the parting
 * for a simple crossfade (the halves fade out instead of sliding).
 *
 * `curtainSrc` supplies curtain art (drawn on both halves, the right mirrored);
 * absent, a layered blood-velvet CSS gradient placeholder stands in. Authored to
 * fill its positioned parent (a BroadcastFrame).
 */

export interface CurtainRevealProps {
  /** When true, the curtains are parted (content revealed). */
  open: boolean;
  /** Curtain art; falls back to a CSS blood-velvet placeholder. */
  curtainSrc?: string;
  children?: ReactNode;
}

const VELVET =
  'repeating-linear-gradient(90deg, var(--blood-1, #320001) 0px, var(--blood-2, #66101f) 22px, var(--ink-1, #0b0709) 44px, var(--blood-1, #320001) 66px)';

function CurtainRevealImpl({ open, curtainSrc, children }: CurtainRevealProps) {
  const reduced = useReducedMotion();

  const halfBase: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '52%', // slight overlap at the seam so no gap shows when closed
    ...(curtainSrc
      ? { backgroundImage: `url(${curtainSrc})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : { background: VELVET }),
    boxShadow: 'inset 0 0 120px rgba(0,0,0,0.6)',
    zIndex: 2,
  };

  // Motion: slide off-side when open; reduced-motion → fade instead.
  const leftAnim = reduced
    ? { opacity: open ? 0 : 1 }
    : { x: open ? '-102%' : '0%', opacity: 1 };
  const rightAnim = reduced
    ? { opacity: open ? 0 : 1 }
    : { x: open ? '102%' : '0%', opacity: 1 };
  const transition = { duration: reduced ? 0.3 : 1.2, ease: [0.7, 0, 0.16, 1] as const };

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {/* Revealed content behind the curtains. */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>{children}</div>

      {/* Gold valance across the top. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 64,
          zIndex: 3,
          background: 'linear-gradient(180deg, var(--gold-4, #c9923f) 0%, var(--gold-1, #57351a) 100%)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
        }}
      />

      <motion.div aria-hidden style={{ ...halfBase, left: 0 }} animate={leftAnim} transition={transition} />
      <motion.div
        aria-hidden
        style={{ ...halfBase, right: 0, transform: 'scaleX(-1)' }}
        animate={rightAnim}
        transition={transition}
      />
    </div>
  );
}

export const CurtainReveal = memo(CurtainRevealImpl);
